/**
 * Geometry/appearance measurement for the render-based design-quality lint rules. The composition is
 * loaded in an off-screen iframe; this module seeks its timeline to a set of probe points and, at
 * each, records every rendered element's box, glyph region, effective opacity, and text appearance.
 * The runtime rules then consume these frame snapshots as pure data. Ported from the codeo layout
 * measurer.
 */

// Tags whose text/content is never rendered as on-screen copy.
const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
]);
const TEXT_NODE = 3;

export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ElementMeasurement {
    /** Live element reference, so rules can read attributes / walk ancestors for opt-outs. */
    element: Element;
    /** Border-box in canvas pixel space. */
    rect: Rect;
    /** The glyph/content region (union of the element's text range rects), for overlap/contrast. */
    inkRect: Rect;
    /** Cumulative opacity down the ancestor chain (0..1). */
    opacity: number;
    /** Computed font-size in px. */
    fontSizePx: number;
    /** Computed foreground colour. */
    color: string;
    /** Whether the computed font-weight is bold (>= 600). */
    bold: boolean;
    /** Whether the element renders its own non-whitespace text directly. */
    hasText: boolean;
}

export interface FrameSnapshot {
    /** Timeline progress this frame was captured at (0..1). */
    progress: number;
    elements: ElementMeasurement[];
    /** Rasterize this frame to a canvas on demand (re-seeks first). Used only by the contrast rule. */
    rasterize(): Promise<HTMLCanvasElement>;
}

/** The rendered canvas size, read from the loaded document's viewport. */
export function canvasSizeOf(document: Document): { width: number; height: number } {
    return {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
    };
}

/** Cumulative opacity: the product of the element's and every ancestor's computed opacity. */
export function effectiveOpacity(el: Element): number {
    const win = el.ownerDocument?.defaultView;
    let opacity = 1;
    let cur: Element | null = el;
    while (cur) {
        const raw =
            (win ? win.getComputedStyle(cur).opacity : '') ||
            (cur as HTMLElement).style?.opacity ||
            '';
        const v = parseFloat(raw);
        if (Number.isFinite(v)) opacity *= v;
        cur = cur.parentElement;
    }
    return opacity;
}

function toRect(box: { left: number; top: number; width: number; height: number }): Rect {
    return { x: box.left, y: box.top, width: box.width, height: box.height };
}

// The union of the element's border box and its text-range box, so a text element whose container
// is large still measures its actual glyph extent (the codeo getInkRect trick).
function inkRectOf(el: Element): Rect {
    const box = el.getBoundingClientRect();
    const doc = el.ownerDocument;
    if (!doc) return toRect(box);
    try {
        const range = doc.createRange();
        range.selectNodeContents(el);
        const rangeRect = range.getBoundingClientRect();
        range.detach();
        if (rangeRect.width === 0 && rangeRect.height === 0) return toRect(box);
        const left = Math.min(box.left, rangeRect.left);
        const top = Math.min(box.top, rangeRect.top);
        const right = Math.max(box.right, rangeRect.right);
        const bottom = Math.max(box.bottom, rangeRect.bottom);
        return { x: left, y: top, width: right - left, height: bottom - top };
    } catch {
        return toRect(box);
    }
}

/**
 * The copy this element renders *itself* — its direct text-node children only, whitespace-collapsed.
 * Descendant text belongs to the descendants, so a wrapper reads as having no copy of its own.
 */
export function ownElementText(el: Element): string {
    return Array.from(el.childNodes)
        .filter((node) => node.nodeType === TEXT_NODE)
        .map((node) => node.textContent ?? '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function rendersOwnText(el: Element): boolean {
    return ownElementText(el).length > 0;
}

// How much of an element's copy to quote when naming it in a finding.
const DESCRIBE_TEXT_MAX = 32;

/**
 * How a finding names the element it is about. An id is best, but plenty of composition elements
 * don't have one, and a bare `<span>` is unfindable in a document full of spans — an author faced
 * with one has to guess, and guessing costs a turn. So fall back to the first class name and a
 * snippet of the element's own copy, either of which can be searched for directly in the markup.
 *
 * Only the element's own text is quoted, never a descendant's: every element in the document gets
 * measured, so the geometry rules routinely report on wrappers, and borrowing a child's words would
 * send the author to the child instead of the element the finding is about.
 */
export function describeElement(element: Element): string {
    const tag = element.tagName.toLowerCase();
    if (element.id) {
        return `<${tag}#${element.id}>`;
    }
    // `className` is an SVGAnimatedString on SVG elements, so only trust it when it's a string.
    const firstClass =
        typeof element.className === 'string' ? element.className.trim().split(/\s+/)[0] : '';
    const selector = firstClass ? `<${tag}.${firstClass}>` : `<${tag}>`;
    const text = ownElementText(element);
    if (!text) {
        return selector;
    }
    const snippet = text.length > DESCRIBE_TEXT_MAX ? `${text.slice(0, DESCRIBE_TEXT_MAX)}…` : text;
    return `${selector} "${snippet}"`;
}

/** Measure every rendered element in the document at its current (already-seeked) frame. */
export function measureElements(document: Document): ElementMeasurement[] {
    const body = document.body;
    if (!body) return [];
    const view = document.defaultView;
    const measurements: ElementMeasurement[] = [];
    for (const element of Array.from(body.querySelectorAll('*'))) {
        if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') continue;
        const style = view?.getComputedStyle(element);
        const fontSizePx = parseFloat(style?.fontSize ?? '') || 0;
        const weight = parseInt(style?.fontWeight ?? '', 10);
        measurements.push({
            element,
            rect: toRect(element.getBoundingClientRect()),
            inkRect: inkRectOf(element),
            opacity: effectiveOpacity(element),
            fontSizePx,
            color: style?.color ?? '',
            bold: Number.isFinite(weight) && weight >= 600,
            hasText: rendersOwnText(element),
        });
    }
    return measurements;
}

/**
 * The renderer surface this module needs: seek the timeline, read the loaded document, and rasterize.
 * Matches HtmlCompositionRenderer without importing it (keeps this module unit-testable with a fake).
 */
export interface FrameSource {
    seek(progress: number): void;
    rasterize(scale: number): Promise<HTMLCanvasElement>;
    readonly contentDocument: Document | null;
}

export const DEFAULT_PROBE_POINTS = [0.06, 0.25, 0.5, 0.75];

/**
 * Seek the composition to each of the given probe points and capture a measured snapshot at each.
 * `rasterize` on a snapshot re-seeks to that frame before rasterizing, so lazily rasterizing one
 * frame is unaffected by later seeks. Every probe is measured; the caller chooses the probes and
 * passes a single one for a composition with no seekable timeline (where every probe would otherwise
 * measure the same static frame).
 */
export async function captureFrames(
    source: FrameSource,
    probes: number[] = DEFAULT_PROBE_POINTS,
): Promise<FrameSnapshot[]> {
    const document = source.contentDocument;
    if (!document) return [];
    const frames: FrameSnapshot[] = [];
    for (const progress of probes) {
        source.seek(progress);
        frames.push({
            progress,
            elements: measureElements(document),
            rasterize: async () => {
                source.seek(progress);
                return source.rasterize(1);
            },
        });
    }
    return frames;
}
